/**
 * 从树数据中删除指定ID的节点（包括其子树）
 * @param {Object} tree - 树数据（根节点）
 * @param {string|number} targetId - 要删除的节点ID
 * @returns {Object|null} - 删除后的树（如果根节点被删除则返回null）
 */
export const removeNode = (tree, targetId) => {
  // 如果树为空或根节点就是要删除的节点
  if (!tree) return null
  if (tree.id === targetId) return null

  // 递归遍历子节点
  if (tree.children && tree.children.length) {
    // 使用filter过滤掉目标节点
    tree.children = tree.children.filter(child => {
      // 如果当前子节点是目标节点，直接过滤掉（包括其子树）
      if (child.id === targetId) {
        return false
      }
      // 递归检查子节点的子树
      const result = removeNode(child, targetId)
      // 如果子节点被删除（即返回null），则过滤掉该子节点
      return result !== null
    })
  }
  return tree
}
/**
 * 从单个树节点中删除指定ID的节点（包括子树）
 * @param {Object} treeNode - 树节点
 * @param {string|number} targetId - 要删除的节点ID
 * @returns {Object|null} - 处理后的树节点（如果被删除则返回null）
 */
export const removeNodeFromTree = (treeNode, targetId) => {
  // 如果节点不存在，直接返回
  if (!treeNode) return null

  // 如果当前节点是目标节点，返回null（表示删除）
  if (treeNode.id === targetId) {
    return null
  }
  // 递归处理子节点
  if (treeNode.children && treeNode.children.length > 0) {
    // 过滤掉被删除的子树
    treeNode.children = treeNode.children
      .map(child => removeNodeFromTree(child, targetId))
      .filter(child => child !== null)
  }

  return treeNode
}

/**
 * 从树数组中删除指定ID的节点（包括子树），返回新数组
 * @param {Array} treeArray - 树数组（每个元素是根节点对象）
 * @param {string|number} targetId - 要删除的节点ID
 * @returns {Array} - 新的树数组（当删除后数组为空时返回空数组 []）
 */
export const removeNodeFromTreeArray = (treeArray, targetId) => {
  // 确保输入是数组
  if (!Array.isArray(treeArray)) {
    return []
  }
  // 深拷贝整个树数组
  const clonedArray = JSON.parse(JSON.stringify(treeArray))
  // 处理每个根节点
  const result = clonedArray
    .map(node => removeNodeFromTree(node, targetId))
    .filter(node => node !== null)
  // 如果结果为空，返回空数组
  return result.length === 0 ? [] : result
}


/**
 * 在单棵树中查找目标节点并插入子节点（使用迭代方式）
 * @param {Object} rootNode - 树的根节点
 * @param {string|number} targetId - 目标节点ID
 * @param {Object} newNode - 新子节点（必须包含id属性）
 * @returns {boolean} - 是否成功插入
 */
export const insertChildNodeInTree = (rootNode, targetId, newNode) => {
  if (!rootNode) return false

  // 使用栈进行迭代遍历（避免递归）
  const stack = [rootNode]

  while (stack.length > 0) {
    const node = stack.pop()

    // 如果找到目标节点，插入新子节点
    if (node.id === targetId) {
      if (!node.children) {
        node.children = []
      }
      node.children.push(newNode)
      return true
    }

    // 将子节点压入栈（从右到左，保持遍历顺序）
    if (node.children) {
      for (let i = node.children.length - 1; i >= 0; i--) {
        stack.push(node.children[i])
      }
    }
  }

  return false
}

/**
 * 在树数组中指定节点的子级位置插入新节点
 * @param {Array} treeArray - 树数组（多个根节点的数组）
 * @param {string|number} targetId - 目标节点ID（要插入到该节点的子级位置）
 * @param {Object} newNode - 要插入的新子节点对象（必须包含id属性）
 * @returns {Array} - 新的树数组（当目标节点不存在时返回原树数组的深拷贝）
 */
export const insertChildNode = (treeArray, targetId, newNode) => {
  if (!Array.isArray(treeArray)) {
    return []
  }
  // 深拷贝确保不修改原数据
  const clonedArray = JSON.parse(JSON.stringify(treeArray))

  // 遍历每个根节点
  for (let i = 0; i < clonedArray.length; i++) {
    if (insertChildNodeInTree(clonedArray[i], targetId, newNode)) {
      return clonedArray
    }
  }

  return clonedArray
}

/**
 * 在树数组中指定节点的同级位置插入新节点
 * @param {Array} treeArray - 树数组（多个根节点的数组）
 * @param {string|number} targetId - 目标节点ID（要插入到该节点的同级位置）
 * @param {Object} newNode - 要插入的新节点对象（必须包含id属性）
 * @returns {Array} -插入成功返回新的树数组（当目标节点不存在时返回原树数组）
 */
export const insertSiblingNode = (treeArray, targetId, newNode) => {
  // 确保输入是数组
  if (!Array.isArray(treeArray)) {
    return []
  }
  // 遍历每个根节点
  for (let i = 0; i < treeArray.length; i++) {
    if (treeArray[i].id === targetId) {
      treeArray.splice(i + 1, 0, newNode)
      return treeArray
    } else if (treeArray[i].children && treeArray[i].children.length) {
      const oldLength = treeArray[i].children.length
      let siblingNode = insertSiblingNode(treeArray[i].children, targetId, newNode)
      if (oldLength !== siblingNode.length) {
        return treeArray
      }
    }
  }
  return treeArray
}
/**
 * 在树数组中指定节点替换数据
 * @param {Array} treeArray - 树数组（多个根节点的数组）
 * @param {string|number} targetId - 目标节点ID（要插入到该节点的同级位置）
 * @param {Object} newNode - 要插入的新节点对象（必须包含id属性）
 * @returns {Array} -插入成功返回新的树数组（当目标节点不存在时返回原树数组）
 */
export const replaceNode = (treeArray, targetId, newNode) => {
  if (!Array.isArray(treeArray)) {
    return []
  }
  for (let i = 0; i < treeArray.length; i++) {
    if (treeArray[i].id === targetId) {
      treeArray[i] = newNode
    } else if (treeArray[i].children && treeArray[i].children.length) {
      treeArray[i].children = replaceNode(treeArray[i].children, targetId, newNode)
    }
  }
  return treeArray
}

/**
 * 交换位置函数,返回交互结果，true为成功，false为失败
 * @param id 选择目标ID
 * @param isMoveUp 是否是向上移动
 * @param list 源数据
 */
const swapPositions = (
  id,
  isMoveUp,
  list
) => {
  if (!id || !list || list?.length < 2) {
    return false
  }
  //获取有效的数据
  const effectiveList = list
  if (!effectiveList || effectiveList.length < 2) {
    return false
  }
  const findIndex = effectiveList.findIndex(
    (item) => item.id === id
  )
  if (findIndex < 0) {
    return false
  }
  if (isMoveUp) {
    if (findIndex === 0) {
      return false
    }
    const currentItem = effectiveList[findIndex]
    const lastItem = effectiveList[findIndex - 1]
    const realLastIndex = list.findIndex(item => item.id === lastItem.id)
    const realCurrentIndex = list.findIndex(item => item.id === currentItem.id)
    list[realLastIndex] = currentItem
    list[realCurrentIndex] = lastItem
    return true
  } else {
    if (findIndex === effectiveList.length - 1) {
      //最后一个无法下移
      return false
    }
    const nextItem = effectiveList[findIndex + 1]
    const currentItem = effectiveList[findIndex]
    const realNextIndex = list.findIndex(item => item.id === nextItem.id)
    const realCurrentIndex = list.findIndex(item => item.id === currentItem.id)
    list[realNextIndex] = currentItem
    list[realCurrentIndex] = nextItem
    return true
  }
}
/**
 * 更新排序
 */
export const updateSorting = (id, isMoveUp, list) => {
  if (!list) {
    return false
  }
  if (list && list.length > 1) {
    const success = swapPositions(id, isMoveUp, list)
    if (success) {
      return true
    }
  }

  const size = list.length
  for (let i = 0; i < size; i++) {
    let item = list[i]
    const success = swapPositions(id, isMoveUp, item.children)
    if (success) {
      return true
    } else {
      if (item.children && item.children.length) {
        const length = item.children.length
        for (let j = 0; j < length; j++) {
          let childItem = item.children[j]
          if (updateSorting(id, isMoveUp, childItem.children)) {
            return true
          }
        }
      }
    }
  }
  return false
}
